// src/components/sections/Contact.jsx
import React, { useState } from 'react';
import { useTranslation } from '../../i18n';
import SectionTag from '../common/SectionTag';

const CONTACT_EMAIL = 'pdg@ofdev.org';
const FORMSPREE_URL = 'https://formspree.io/f/mzdlbenb';

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  const contactInfo = [
    { icon: '📍', labelKey: 'contact_address_label', valueKey: 'contact_address' },
    { icon: '📞', labelKey: 'contact_phone_label', valueKey: 'contact_phone' },
    { icon: '✉️', labelKey: 'contact_email_label', value: CONTACT_EMAIL },
    { icon: '📄', labelKey: 'contact_auth_label', valueKey: 'contact_auth' },
  ];

  const formFields = [
    { key: 'form_name', placeholderKey: 'form_name_placeholder', type: 'text', name: 'name' },
    { key: 'form_email', placeholderKey: 'form_email_placeholder', type: 'email', name: 'email' },
    { key: 'form_subject', placeholderKey: 'form_subject_placeholder', type: 'text', name: 'subject' },
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await fetch(FORMSPREE_URL, {
        method: 'POST',
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          _subject: `Nouveau message de ${formData.name}`,
          _replyto: formData.email
        }),
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        setStatus({
          type: 'success',
          message: `✅ Votre message a été envoyé avec succès à ${CONTACT_EMAIL} ! Nous vous répondrons dans les plus brefs délais.`
        });
        // Réinitialiser le formulaire
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Erreur:', error);
      setStatus({
        type: 'error',
        message: `❌ Une erreur est survenue: ${error.message}. Veuillez réessayer ou nous contacter directement à ${CONTACT_EMAIL}.`
      });
    }

    setIsLoading(false);
  };

  return (
    <section className="bg-white px-4 md:px-10 py-20" id="contact">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
        {/* Colonne gauche - Informations de contact */}
        <div>
          <SectionTag>{t('contact_tag')}</SectionTag>
          <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-8">
            {t('contact_title')}
          </h2>
          {contactInfo.map((info, index) => (
            <div key={index} className="flex gap-3.5 items-start mb-5">
              <div className="w-9 h-9 flex-shrink-0 bg-ofd-gray-light rounded-full flex items-center justify-center text-base">
                {info.icon}
              </div>
              <div>
                <small className="block text-ofd-gray text-xs font-semibold uppercase tracking-wider">
                  {info.labelKey ? t(info.labelKey) : ''}
                </small>
                <span className="text-ofd-slate text-sm" dangerouslySetInnerHTML={{
                  __html: info.valueKey ? t(info.valueKey) : info.value || '',
                }} />
              </div>
            </div>
          ))}
          {/* Badge domaine professionnel */}
            
        </div>

        {/* Colonne droite - Formulaire */}
        <div>
          <SectionTag>{t('message_tag')}</SectionTag>
          <h2 className="font-playfair text-3xl md:text-4xl font-bold">{t('message_title')}</h2>
          
          {status.message && (
            <div className={`mt-4 p-4 rounded-lg ${
              status.type === 'success' 
                ? 'bg-green-100 text-green-800 border border-green-300' 
                : 'bg-red-100 text-red-800 border border-red-300'
            }`}>
              {status.message}
            </div>
          )}
          
          <form className="flex flex-col gap-4 mt-4" onSubmit={handleSubmit}>
            {formFields.map((field) => (
              <div key={field.key} className="flex flex-col gap-1.5">
                <label className="text-ofd-gray text-xs font-semibold tracking-wide">
                  {t(field.key)}
                </label>
                <input
                  type={field.type}
                  name={field.name}
                  value={formData[field.name]}
                  onChange={handleChange}
                  placeholder={t(field.placeholderKey)}
                  required
                  className="px-4 py-3 border border-ofd-gray-light rounded bg-ofd-cream text-ofd-slate text-sm outline-none transition-colors focus:border-ofd-blue"
                />
              </div>
            ))}

            <div className="flex flex-col gap-1.5">
              <label className="text-ofd-gray text-xs font-semibold tracking-wide">
                {t('form_message')}
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder={t('form_message_placeholder')}
                rows={4}
                required
                className="px-4 py-3 border border-ofd-gray-light rounded bg-ofd-cream text-ofd-slate text-sm outline-none transition-colors focus:border-ofd-blue resize-y min-h-[120px]"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="inline-block px-7 py-3 bg-ofd-yellow text-ofd-slate font-semibold rounded-[3px] text-sm tracking-wide transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed self-start"
            >
              {isLoading ? '⏳ Envoi en cours...' : t('form_submit')}
            </button>

            <p className="text-xs text-ofd-gray mt-2">
              📧 Les messages sont envoyés via Formspree et reçus sur <strong>{CONTACT_EMAIL}</strong>
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;